import { Badge, Button, Card } from "react-bootstrap"
import { Link } from "react-router-dom"
import { TurnoverType } from "../../../Types/turnoverTypes"
import useUserRefs from "../../../Hooks/UseUsersQuery/UseUserRefs"
import { useState } from "react"
import canAccess from "../../../services/accessService/accessService"
import timeService from "../../../services/time/timeService"
import PropertyTitleText from "../../PropertyEntry/PropertyTitleText"

export default function TurnoverEntry(props: {
    turnoverData: TurnoverType,
    fullView?: boolean,
    showActions?: boolean,
    customButtons?: {
        onClick: (turnoverData: TurnoverType) => void,
        text: string,
        variant?: string,
        disabled?: boolean,
    }[]
}) {

    const {
        turnoverData,
        fullView: initialFullView = false,
        showActions = true,
        customButtons,
    } = props

    const [fullView, setFullView] = useState(initialFullView)
    const userRefs = useUserRefs()

    const {
        _id,
        propertyId,
        bookingId,
        type,
        status,
        scheduledFor,
        assignedTo,
        completedAt,
        completedBy,
        notes,
        createdAt,
        createdBy,
    } = turnoverData

    const getStatusVariant = (status: string) => {
        switch (status) {
            case 'completed': return 'success'
            case 'ready': return 'info'
            case 'started': return 'warning'
            case 'pending': return 'secondary'
            default: return 'secondary'
        }
    }

    const getUserDisplayName = (userId: string) => {
        if (userRefs[userId]) {
            return `${userRefs[userId].firstName} ${userRefs[userId].lastName}`
        }
        return userId
    }

    return (
        <Card className="mb-3">
            <Card.Body>
                <Card.Title>
                    {type}
                </Card.Title>
                <Card.Subtitle className="mb-2 text-muted">
                    <PropertyTitleText propertyId={propertyId} shortName={true} />
                </Card.Subtitle>

                <Card.Text className="mb-1 small">
                    <strong>Scheduled For:</strong> {timeService.getFormattedTime(scheduledFor)}
                </Card.Text>
                {createdAt && (
                    <Card.Text className="mb-1 small">
                        <strong>Created:</strong> {timeService.getFormattedTime(createdAt)}
                    </Card.Text>
                )}

                {fullView && (
                    <>
                        <hr />
                        <Card.Subtitle className="mb-2">
                            <strong>Details:</strong>
                        </Card.Subtitle>
                        <Card.Text className="mb-1 small">
                            <strong>Property ID:</strong> <Link className="ms-2" to={`/property/${propertyId}`}>
                                {propertyId}
                            </Link>
                        </Card.Text>
                        {bookingId && (
                            <Card.Text className="mb-1 small">
                                <strong>Booking ID:</strong> <Link className="ms-2" to={`/booking/${bookingId}`}>
                                    {bookingId}
                                </Link>
                            </Card.Text>
                        )}
                        {createdBy && (
                            <Card.Text className="mb-1 small">
                                <strong>Created By:</strong> {getUserDisplayName(createdBy)}
                            </Card.Text>
                        )}
                        {assignedTo && (
                            <Card.Text className="mb-1 small">
                                <strong>Assigned To:</strong> {getUserDisplayName(assignedTo)}
                            </Card.Text>
                        )}
                        {completedAt && (
                            <Card.Text className="mb-1 small">
                                <strong>Completed At:</strong> {timeService.getFormattedTime(completedAt)}
                            </Card.Text>
                        )}
                        {completedBy && (
                            <Card.Text className="mb-1 small">
                                <strong>Completed By:</strong> {getUserDisplayName(completedBy)}
                            </Card.Text>
                        )}
                        {notes && (
                            <Card.Text className="mb-1 small">
                                <strong>Notes:</strong><br />
                                {notes}
                            </Card.Text>
                        )}
                    </>
                )}

                {!fullView && notes && (
                    <Card.Text className="mb-2 small text-muted">
                        Notes available
                    </Card.Text>
                )}

            </Card.Body>

            {showActions && (
                <Card.Footer>

                    <div>
                        <Badge bg={getStatusVariant(status)} className="me-2 mb-2">
                            {status}
                        </Badge>
                    </div>

                    {!initialFullView && canAccess('routes', 'turnover') && (
                        <Link className="btn btn-primary btn-sm me-2 mb-2" to={`/turnover/${_id}`}>
                            View
                        </Link>
                    )}
                    {canAccess('routes', 'edit-turnover') && (
                        <Link className="btn btn-secondary btn-sm me-2 mb-2" to={`/edit-turnover/${_id}`}>
                            Edit
                        </Link>
                    )}
                    {canAccess('routes', 'delete-turnover') && (
                        <Link className="btn btn-danger btn-sm me-2 mb-2" to={`/delete-turnover/${_id}`}>
                            Delete
                        </Link>
                    )}
                    {!initialFullView && (
                        <Button
                            className="me-2 mb-2"
                            variant="secondary"
                            size="sm"
                            onClick={() => setFullView(!fullView)}
                        >
                            {fullView ? 'Hide Details' : 'Show Details'}
                        </Button>
                    )}
                    {customButtons && customButtons.map((btn, idx) => (
                        <Button
                            key={`custom-btn-${idx}`}
                            className="me-2 mb-2"
                            variant={btn.variant || 'primary'}
                            size="sm"
                            onClick={() => btn.onClick(turnoverData)}
                            disabled={btn.disabled}
                        >
                            {btn.text}
                        </Button>
                    ))}
                </Card.Footer>
            )}
        </Card>
    )
}
